// DeliveryInfo.tsx
import React, { useEffect, useState } from "react";
import { SelectedProduct } from "./types";
import defaultAvatar from "../../../../../assets/images/default_avatar.jpg";
import OrderSummary from "./OrderSummary";
import toast from "react-hot-toast";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import { Order } from "../OrderInfo/types";
import { PrescriptionCardHandle } from "./PrescriptionCard";
import useGeneralNotification from "../../../../../components/patients/hooks/useNotifcation";
import { useAuth } from "../../../../../context/AuthContext";
import { AuthService } from "../../../../../services/api";
import { OrderService } from "../../../../../services/api/order.service";

interface DeliveryInfoProps {
  selectedProducts: SelectedProduct[];
  onNotifyComplete: () => void;
  address?: string;
  pincode?: string;
  orderDate?: string;
  patientId?: string;
  selectedOrder?: Order;
  prescriptionCardRef?: React.RefObject<PrescriptionCardHandle>;
  fetchOrder: () => void;
  doctorName: string | undefined;
  setIsDoctorRequired: React.Dispatch<React.SetStateAction<boolean>>;
  isDoctorRequired: boolean;
}

interface PatientDetails {
  name: string;
  mobile_number: string;
  profile_image?: string;
}

const DeliveryInfo: React.FC<DeliveryInfoProps> = ({
  selectedProducts,
  onNotifyComplete,
  address,
  pincode,
  orderDate,
  patientId,
  selectedOrder,
  prescriptionCardRef,
  fetchOrder,
  doctorName,
  setIsDoctorRequired,
  isDoctorRequired,
}) => {
  const [patientDetails, setPatientDetals] =
    React.useState<PatientDetails | null>(null);
  const { user } = useAuth();

  const { sendNotifications } = useGeneralNotification({
    toId: String(user?.id),
    notify: false,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const calculateTotals = () => {
    console.log("Order Items:", selectedOrder?.productOrderItems);
    const orderItems = selectedOrder?.productOrderItems ?? [];
    // Add this in DeliveryInfo.tsx's calculateTotals()
    const hasOrderProductItems = orderItems.length > 0;

    if (hasOrderProductItems) {
      const totalItems = orderItems.reduce(
        (sum, item) => sum + (item.quantity || 1),
        0
      );

      const totalAmount = orderItems.reduce((sum, item) => {
        // Use the final_selling_price from the ordered products
        const price = parseFloat(item.finalSP) || 0;
        return sum + price * (item.quantity || 1);
      }, 0);

      return { totalItems, totalAmount, hasOrderProductItems };
    }

    // For selected products (not yet ordered) use landing_cost
    const totalItems = selectedProducts.reduce(
      (sum, product) => sum + product.selectedQuantity,
      0
    );

    const totalAmount = selectedProducts.reduce(
      (sum, product) => sum + product.finalSP * product.selectedQuantity,
      0
    );

    return { totalItems, totalAmount, hasOrderProductItems: false };
  };

  const { totalItems, totalAmount, hasOrderProductItems } = calculateTotals();

  const handleApproveOrder = async () => {
    try {
      if (!selectedOrder?.id) {
        throw new Error("No order ID available");
      }

      const check = await OrderService.checkOrder(selectedOrder?.id);
      if (check && !doctorName) {
        throw new Error("Doctor name required");
      }
      const orderId = selectedOrder.id;
      let prescriptionImage: File | string | null = null;
      let hasPrescription = false;

      if (selectedOrder.prescriptionDetails?.prescriptionImage) {
        console.log("Using original prescription image...");
        hasPrescription = true;
        const originalImage =
          selectedOrder.prescriptionDetails.prescriptionImage;

        if (originalImage.startsWith("data:")) {
          try {
            const blob = await fetch(originalImage).then((res) => res.blob());
            prescriptionImage = new File([blob], "prescription.png", {
              type: "image/png",
            });
          } catch (error) {
            console.error("Error converting original image to File:", error);
            // Fallback to sending as string if conversion fails
            prescriptionImage = originalImage;
          }
        } else {
          // If it's already a file URL or path
          prescriptionImage = originalImage;
        }
      }

      // Only fallback to screenshot if no original image available
      if (!prescriptionImage && prescriptionCardRef?.current) {
        console.log("Falling back to screenshot...");
        const capturedImage =
          await prescriptionCardRef.current.capturePrescriptionImage();
        if (capturedImage) {
          hasPrescription = true;
          const blob = await fetch(capturedImage).then((res) => res.blob());
          prescriptionImage = new File([blob], "prescription_screenshot.png", {
            type: "image/png",
          });
        }
      }

      const res = await AdminOrderService.processOrderSuccess(
        orderId,
        hasOrderProductItems ? selectedOrder?.orderTotal : totalAmount,
        hasPrescription,
        prescriptionImage,
        doctorName
      );

      if (res.success) {
        const notificationData = {
          id: String(orderId),
          to: "To Clinic",
          type: "Order",
          title: "New Order",
          message: `New order Assigned #${res.data.order_number}`,
          toId: res.data.clinic_id,
          status: "Unread",
          order_number: res.data.order_number,
          order_type: "Assigned order",
          order_date: res.data.order_date,
        };

        await sendNotifications(notificationData);
        toast.success("Order processed successfully!");
        setTimeout(() => {
          onNotifyComplete();
          fetchOrder();
        }, 2000);
      }
    } catch (error) {
      console.error("Error processing order:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to process order"
      );
    }
  };

  const checkDoctorRequired = async (products: any[]) => {
    for (const product of products) {
      const check = await OrderService.checkProduct(product.product_id);
      if (check) {
        setIsDoctorRequired(true);
        return true;
      }
    }
    return false;
  };
  const handleNotifyPatientWithAddedItems = async () => {
    try {
      if (!selectedOrder?.id) throw new Error("No order ID available");
      const check = await checkDoctorRequired(selectedProducts);

      if (check && !doctorName) {
        toast.error("Doctor Name is required for these medicines");
        return;
      }
      const orderId = selectedOrder.id;
      const res = await AdminOrderService.addOrderItemByAdmin(
        orderId,
        totalAmount,
        selectedProducts,
        doctorName
      );

      if (!res.success) throw new Error("Failed to add items to the order");

      const { patient_id: patientId, order_number: orderNumber } =
        res.data || {};
      const title = "Order Prepared by admin";
      const message = `Your order - ${orderNumber} has been prepared. You are required to complete payment for further processing.`;
      try {
        const tokens = await AuthService.fetchNotificationTokens(patientId);

        await AuthService.sendNotification(title, message, tokens);
      } catch (notifyErr) {
        console.error("Error sending push notification:", notifyErr);
      }
      try {
        await AuthService.createNotification(
          title,
          message,
          patientId,
          "Patient"
        );
      } catch (dbNotifyErr) {
        console.error("Error creating notification record:", dbNotifyErr);
      }

      // 4. Success Flow
      toast.success("Patient notified successfully!");
      setTimeout(() => {
        onNotifyComplete();
        fetchOrder();
      }, 2000);
    } catch (err) {
      console.error("Error in handleNotifyPatientWithAddedItems:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to process order"
      );
    }
  };

  useEffect(() => {
    if (!patientId) return;

    const getPatientDetails = async () => {
      try {
        const response = await AdminOrderService.getAdminPatientDetailsById(
          patientId
        );
        setPatientDetals(response.data);
      } catch (error) {
        console.log(error);
      }
    };

    getPatientDetails();
  }, [patientId]);

  return (
    <div className="mb-6 w-full">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-red-500">📍</span>
        <span className="font-medium text-gray-700">Delivery Address</span>
      </div>
      <div className="text-sm text-gray-600 ml-6">
        <div>{address || "Address not specified"}</div>
        <div> {pincode && `Pin Code: ${pincode}`}</div>
      </div>

      <div className="mt-4">
        {/* Ordered By */}
        <div className="mb-4 xl:mb-0 w-full xl:w-auto">
          <div className="mt-3 mb-4">
            <p className="text-gray-700 mb-2">Ordered by</p>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                <img
                  src={
                    patientDetails?.profile_image
                      ? patientDetails.profile_image
                      : defaultAvatar
                  }
                  alt="user"
                  className="w-8 h-8 rounded-full"
                />
              </div>
              <div>
                <p className="font-medium">{patientDetails?.name}</p>
                <p className="text-gray-500 text-xs">
                  {" "}
                  {patientDetails?.mobile_number}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-400 mt-1 mb-2">
          Order Date: {orderDate ? formatDate(orderDate) : "Not available"}
        </div>
      </div>

      <OrderSummary
        order={selectedOrder}
        totalItems={totalItems}
        totalAmount={
          hasOrderProductItems ? selectedOrder?.orderTotal : totalAmount
        }
        onNotifyPatient={
          hasOrderProductItems
            ? handleApproveOrder
            : handleNotifyPatientWithAddedItems
        }
        actionText={hasOrderProductItems ? "Approve Order" : "Notify Patient"}
      />
    </div>
  );
};

export default DeliveryInfo;
